import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import { convertWeight, TouchNumberField } from '@rematter/ui-web';
import Big from 'big.js';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $lastFocusedRowId,
  $lastFocusedWeightFieldType,
} from '../../../../_state/loadStaged.state';
import { useTakeWeightListener } from '../../../../hooks/useScaleReading';
import { useScaleTakeWeight } from '../../../../hooks/useScaleWeight';
import { useWeightConversion } from '../../../../hooks/useWeightConversion';
import { LoadStagedFieldProps } from '../_types';

export const GrossWeightField: React.FC<LoadStagedFieldProps> = ({
  lineItem,
  disabled,
  onUpdateItem,
  touchModeEnabled,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  onFocus,
  onBlur,
}) => {
  // Use lastFocusedRowId instead of focusedRowId to handle button clicks
  // (focus moves to button before event is emitted)
  const lastFocusedRowId = useAtomValue($lastFocusedRowId);
  const lastFocusedWeightFieldType = useAtomValue($lastFocusedWeightFieldType);
  const setLastFocusedWeightFieldType = useSetAtom($lastFocusedWeightFieldType);

  const { takeWeight } = useScaleTakeWeight();

  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: lineItem.pricingUnit as Unit,
    });

  // Get the stored unit for weight values (defaults to Pounds)
  const storedUnit =
    (lineItem.weights?.[enumLoadWeightType.Gross]?.unit as Unit) ||
    enumUnit.Pounds;

  // Updates gross weight from a display unit value (used by manual input)
  const handleGrossWeightChange = React.useCallback(
    (value: number) => {
      const convertedValue = convertFromDisplayUnit(value, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Gross]: {
            value: convertedValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
          [enumLoadWeightType.Net]: {
            value: new Big(convertedValue)
              .minus(lineItem.weights?.[enumLoadWeightType.Tare]?.value ?? 0)
              .toNumber(),
            unit: storedUnit,
            __typename: 'Weight',
          },
        },
      });
    },
    [
      lineItem.id,
      lineItem.weights,
      storedUnit,
      convertFromDisplayUnit,
      onUpdateItem,
    ],
  );

  const applyScaleReading = React.useCallback(
    (scaleValue: number, scaleUnit: Unit) => {
      const convertedValue = convertWeight(scaleValue, scaleUnit, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Gross]: {
            value: convertedValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
          [enumLoadWeightType.Net]: {
            value: new Big(convertedValue)
              .minus(lineItem.weights?.[enumLoadWeightType.Tare]?.value ?? 0)
              .toNumber(),
            unit: storedUnit,
            __typename: 'Weight',
          },
        },
      });
    },
    [lineItem.id, lineItem.weights, storedUnit, onUpdateItem],
  );

  useTakeWeightListener(
    ({ weight, unitOfMeasurement }) => {
      if (
        lastFocusedRowId === lineItem.id &&
        lastFocusedWeightFieldType === 'gross'
      ) {
        applyScaleReading(weight, unitOfMeasurement);
      }
    },
    [
      lastFocusedRowId,
      lastFocusedWeightFieldType,
      lineItem.id,
      applyScaleReading,
    ],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ') {
        e.preventDefault();
        const reading = takeWeight();
        if (reading?.stable) {
          applyScaleReading(
            reading.grossWeight,
            reading.unitOfMeasurement ?? enumUnit.Pounds,
          );
        }
      }
    },
    [takeWeight, applyScaleReading],
  );

  const handleFocus = React.useCallback(() => {
    setLastFocusedWeightFieldType('gross');
    onFocus?.();
  }, [setLastFocusedWeightFieldType, onFocus]);

  const isQuantityBased = lineItem.pricingUnit === enumUnit.Each;

  return (
    <TouchNumberField
      aria-label="Gross weight"
      isDisabled={lineItem.type === 'Tagged' || disabled}
      value={convertToDisplayUnit(lineItem.weights?.[enumLoadWeightType.Gross])}
      onChange={handleGrossWeightChange}
      onKeyDown={handleKeyDown}
      formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
      touchModeEnabled={touchModeEnabled}
      variant="outlined"
      suffix={massUnitAbbreviation}
      prefix={isQuantityBased ? undefined : 'G'}
      onFocus={handleFocus}
      onBlur={onBlur}
      className="@GrossWeightField"
    />
  );
};
